import React from 'react';
import styled from 'styled-components';

import { useAdminGetDashboardVenuesDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardVenuesDataQuery';
import { icons } from '../../themes/icons';
import { calculatePercentageChange } from '../../utils/dashboard';
import { toLocaleString } from '../../utils/format-number';
import { CardError } from './CardError';
import StatCard from './StatCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  selectedLabel: string;
  marketID: string | null;
};

export const VenueStats: React.FC<Props> = ({
  fromDate,
  toDate,
  priorFrom,
  priorTo,
  selectedLabel,
  marketID,
}) => {
  const { data, isFetching, isError, refetch } = useAdminGetDashboardVenuesDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID,
  });

  const {
    venueAppPercentage,
    totalNewActivePercentage,
    totalRejectedVenuesPercentage,
    totalVenuesWithReservationsPercentage,
  } = React.useMemo(() => {
    const venueAppPercentage = calculatePercentageChange(
      data?.totalVenueApplicationsCurrent || 0,
      data?.totalVenueApplicationsPrior || 0,
    );

    const totalNewActivePercentage = calculatePercentageChange(
      data?.totalNewActiveVenuesCurrent || 0,
      data?.totalNewActiveVenuesPrior || 0,
    );

    const totalRejectedVenuesPercentage = calculatePercentageChange(
      data?.totalRejectedVenuesCurrent || 0,
      data?.totalRejectedVenuesPrior || 0,
    );

    const totalVenuesWithReservationsPercentage = calculatePercentageChange(
      data?.totalVenuesWithReservationsCurrent || 0,
      data?.totalVenuesWithReservationsPrior || 0,
    );

    return {
      venueAppPercentage,
      totalNewActivePercentage,
      totalRejectedVenuesPercentage,
      totalVenuesWithReservationsPercentage,
    };
  }, [data]);

  return (
    <BoxContainer>
      <AllStats>
        <Heading>Venues stats</Heading>
        <Time>{selectedLabel}</Time>
      </AllStats>

      {isError && !isFetching && <CardError onRetry={() => refetch()} />}
      {!isError && (
        <>
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Business Applications"
            count={toLocaleString(data?.totalVenueApplicationsCurrent || 0)}
            compare={`${venueAppPercentage}%`}
            icon={<icons.NewVenueApplicationIcon />}
            percentageNumber={venueAppPercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="New venue added"
            count={toLocaleString(data?.totalNewActiveVenuesCurrent || 0)}
            compare={`${totalNewActivePercentage}%`}
            icon={<icons.NewVenueIcon />}
            percentageNumber={totalNewActivePercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Venues lost"
            count={toLocaleString(data?.totalRejectedVenuesCurrent || 0)}
            compare={`${totalRejectedVenuesPercentage}%`}
            icon={<icons.RemoveVenueIcon />}
            percentageNumber={totalRejectedVenuesPercentage}
          />
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Venues with reservations"
            count={toLocaleString(data?.totalVenuesWithReservationsCurrent || 0)}
            compare={`${totalVenuesWithReservationsPercentage}%`}
            icon={<icons.VenueWithReservationIcon />}
            percentageNumber={totalVenuesWithReservationsPercentage}
          />
        </>
      )}
    </BoxContainer>
  );
};

const BoxContainer = styled.div`
  border: 1px solid #00000014;
  border-radius: 8px;
  padding: 30px;
`;
const AllStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding-bottom: 15px;
`;

const Heading = styled.div`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 500;
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;
